import { useMemberSettings } from "@hooks/useMemberSettings";
import { MemberSettings } from "@types";
import { ReactNode } from "react";

// Omit keys that shouldn't be used in the feature prop
type FeatureKey = Exclude<keyof MemberSettings, "themeId">;

type MatchSingle = {
  match: string;
  matchOr?: never;
};

type MatchMultiple = {
  match?: never;
  matchOr: string[];
};

type MemberSettingsRendererProps = {
  feature: FeatureKey;
  children: ReactNode;
} & (MatchSingle | MatchMultiple);

/**
 * Conditionally renders children based on the value of a member setting
 *
 * @example
 * <MemberSettingsRenderer feature="loginMethod" match="pin-mode">
 *   <PinOnboardingFlow />
 * </MemberSettingsRenderer>
 *
 * @example
 * <MemberSettingsRenderer feature="fundingProfile" matchOr={["bank-only", "bank-and-ewallet"]}>
 *   <BankInfoDrawer />
 * </MemberSettingsRenderer>
 */
const MemberSettingsRenderer = ({ feature, match, matchOr, children }: MemberSettingsRendererProps) => {
  const settings = useMemberSettings();

  const value = (() => {
    const settingValue = settings[feature];
    // Handle numeric values
    if (typeof settingValue === "number") {
      return (settingValue as number).toString();
    }
    return settingValue;
  })();

  // Check if value matches either a single match or any in matchOr array
  if (match !== undefined && value === match) {
    return <>{children}</>;
  }
  if (matchOr !== undefined && matchOr.includes(value as string)) {
    return <>{children}</>;
  }

  return null;
};

export default MemberSettingsRenderer;
